import React, { useState, useEffect, useMemo } from 'react';
import { 
  Archive, RotateCcw, Trash2, Search, X, Package, Users, FolderTree,
  AlertTriangle, CheckCircle2, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// SearchInput Component
const SearchInput = ({ value, onChange, placeholder, className = "" }) => (
  <div className={`relative ${className}`}>
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-10 pr-8"
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600">
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

// SortableHeader Component
const SortableHeader = ({ column, label, sortConfig, onSort, className = "" }) => {
  const isSorted = sortConfig?.key === column;
  const direction = sortConfig?.direction;
  const handleClick = () => {
    let newDirection = 'asc';
    if (isSorted) {
      if (direction === 'asc') newDirection = 'desc';
      else if (direction === 'desc') newDirection = null;
    }
    onSort(newDirection ? { key: column, direction: newDirection } : null);
  };
  return (
    <TableHead className={`cursor-pointer hover:bg-stone-50 select-none ${className}`} onClick={handleClick}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isSorted ? (direction === 'asc' ? <ArrowUp className="w-4 h-4 text-lime-600" /> : <ArrowDown className="w-4 h-4 text-lime-600" />) : <ArrowUpDown className="w-4 h-4 text-stone-300" />}
      </div>
    </TableHead>
  );
};

// Utility functions
const sortData = (data, sortConfig) => {
  if (!sortConfig?.key) return data;
  return [...data].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const ArchiveManager = ({ currentUserRole = 'viewer' }) => {
  const [archivedItems, setArchivedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'archived_at', direction: 'desc' });
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [restoreLoading, setRestoreLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const isManager = currentUserRole === 'admin' || currentUserRole === 'manager';
  const isAdmin = currentUserRole === 'admin';

  useEffect(() => {
    fetchArchivedItems();
  }, []);

  const fetchArchivedItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/archive/items`, { withCredentials: true });
      setArchivedItems(response.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch archived items");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (archiveId) => {
    setRestoreLoading(archiveId);
    try {
      await axios.post(`${API}/api/archive/restore/${archiveId}`, {}, { withCredentials: true });
      toast.success("Item restored successfully");
      fetchArchivedItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore item");
    } finally {
      setRestoreLoading(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteDialog.item) return;
    
    setDeleteLoading(deleteDialog.item.id);
    try {
      await axios.delete(`${API}/api/archive/${deleteDialog.item.id}`, { withCredentials: true });
      toast.success("Item permanently deleted");
      setDeleteDialog({ open: false, item: null });
      fetchArchivedItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete item");
    } finally {
      setDeleteLoading(null);
    }
  };

  // Filter and sort data
  const filteredItems = useMemo(() => {
    let data = archivedItems;
    
    // Filter by type
    if (activeTab !== 'all') {
      data = data.filter(item => item.item_type === activeTab);
    }
    
    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(item => {
        const itemData = item.item_data || {};
        return (
          itemData.product_name?.toLowerCase().includes(term) ||
          itemData.vendor_name?.toLowerCase().includes(term) ||
          itemData.name?.toLowerCase().includes(term) ||
          item.archived_by_name?.toLowerCase().includes(term)
        );
      });
    }
    
    // Sort
    return sortData(data, sortConfig);
  }, [archivedItems, activeTab, searchTerm, sortConfig]);

  const getItemName = (item) => {
    const data = item.item_data || {};
    return data.product_name || data.vendor_name || data.name || 'Unknown';
  };

  const getItemIcon = (itemType) => {
    switch (itemType) {
      case 'product': return <Package className="w-4 h-4 text-amber-600" />;
      case 'vendor': return <Users className="w-4 h-4 text-blue-600" />;
      case 'category': return <FolderTree className="w-4 h-4 text-purple-600" />;
      default: return <Archive className="w-4 h-4 text-stone-500" />;
    }
  };

  const getCounts = () => ({
    all: archivedItems.length,
    product: archivedItems.filter(i => i.item_type === 'product').length,
    vendor: archivedItems.filter(i => i.item_type === 'vendor').length,
    category: archivedItems.filter(i => i.item_type === 'category').length
  });

  const counts = getCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Archive className="w-6 h-6" />
            Archive Data
          </h2>
          <p className="text-sm text-stone-500">Manage archived products, vendors, and categories</p>
        </div>
        <Button variant="outline" onClick={fetchArchivedItems} data-testid="refresh-archive-btn">
          <RotateCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs for filtering by type */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="all" data-testid="archive-tab-all">
              All ({counts.all})
            </TabsTrigger>
            <TabsTrigger value="product" data-testid="archive-tab-products">
              <Package className="w-4 h-4 mr-1" />
              Products ({counts.product})
            </TabsTrigger>
            <TabsTrigger value="vendor" data-testid="archive-tab-vendors">
              <Users className="w-4 h-4 mr-1" />
              Vendors ({counts.vendor})
            </TabsTrigger>
            <TabsTrigger value="category" data-testid="archive-tab-categories">
              <FolderTree className="w-4 h-4 mr-1" />
              Categories ({counts.category})
            </TabsTrigger>
          </TabsList>
          
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search archived items..."
            className="w-64"
          />
        </div>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">
                {activeTab === 'all' ? 'All Archived Items' : `Archived ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}s`}
              </CardTitle>
              <CardDescription>
                {filteredItems.length} item(s) in archive
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">Type</TableHead>
                    <SortableHeader column="item_data.product_name" label="Name" sortConfig={sortConfig} onSort={setSortConfig} />
                    <TableHead>Details</TableHead>
                    <SortableHeader column="archived_at" label="Archived Date" sortConfig={sortConfig} onSort={setSortConfig} />
                    <TableHead>Archived By</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-left">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Archive className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                        <p className="text-stone-500">No archived items found</p>
                        <p className="text-sm text-stone-400 mt-1">
                          Items you archive from Products, Vendors, or Categories will appear here
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center justify-center">
                            {getItemIcon(item.item_type)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {getItemName(item)}
                        </TableCell>
                        <TableCell className="text-sm text-stone-500">
                          {item.item_type === 'product' && (
                            <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800">
                              {item.item_data?.category || 'No category'}
                            </span>
                          )}
                          {item.item_type === 'vendor' && (
                            <span>{item.item_data?.contact || 'No contact'}</span>
                          )}
                          {item.item_type === 'category' && (
                            <span>{item.item_data?.description || 'No description'}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(item.archived_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>{item.archived_by_name || 'System'}</TableCell>
                        <TableCell className="text-stone-500 max-w-xs truncate">
                          {item.reason || '—'}
                        </TableCell>
                        <TableCell className="text-left">
                          <div className="flex items-center justify-end gap-2">
                            {isManager && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestore(item.id)}
                                disabled={restoreLoading === item.id}
                                className="text-lime-700 border-lime-300 hover:bg-lime-50"
                                data-testid={`restore-btn-${item.id}`}
                              >
                                {restoreLoading === item.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lime-700" />
                                ) : (
                                  <>
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Restore
                                  </>
                                )}
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, item })}
                                className="text-rose-600 border-rose-300 hover:bg-rose-50"
                                data-testid={`delete-btn-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Permanently Delete Item?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">
                This action <strong>cannot be undone</strong>. The following item will be permanently deleted:
              </p>
              <div className="p-3 bg-stone-50 rounded-lg border">
                <p className="font-medium">{deleteDialog.item && getItemName(deleteDialog.item)}</p>
                <p className="text-sm text-stone-500">
                  Type: {deleteDialog.item?.item_type} | Archived: {deleteDialog.item && new Date(deleteDialog.item.archived_at).toLocaleDateString()}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePermanentDelete}
              className="bg-rose-600 hover:bg-rose-700"
              disabled={deleteLoading}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchiveManager;
